/* eslint-disable @typescript-eslint/no-use-before-define */
import {
  SectionTeacherRow,
  SubjectTypeOption,
  TeacherWorkloadMode,
} from 'src/app/services/models/subjects/subject.model';
import { ScheduleSubmissionUsersByUserId } from '../application/subject-management-create.facade';
import {
  ApprovalTeacherInput,
  SubjectApprovalBuilderInput,
} from './subject-management-create-approval.builder';
import { toNullableString } from './subject-management-create.utils';

export interface ApprovalSectionInput {
  section: unknown;
  teachingType: unknown;
  room: unknown;
  studentCount: unknown;
  teachingTimes: {
    teachingDay: unknown;
    startTime: unknown;
    endTime: unknown;
  }[];
}

export interface ApprovalAdapterInput {
  academicTermId: number | null;
  subjectId: number | null;
  curriculumTypeId: number | null;
  courseunit?: string | null;
  createdById?: number | null;
  section: ApprovalSectionInput;
  studyModeId: number | null;
  studyModeOptions: SubjectTypeOption[];
  isProjectResearch: boolean;
  workloadMode: TeacherWorkloadMode;
  teachers: SectionTeacherRow[];
  scheduleSubmissionUsersByUserId: ScheduleSubmissionUsersByUserId;
}

const THAI_DAY_INDEXES: Partial<Record<string, number>> = {
  'วันอาทิตย์': 0,
  'วันจันทร์': 1,
  'วันอังคาร': 2,
  'วันพุธ': 3,
  'วันพฤหัสบดี': 4,
  'วันศุกร์': 5,
  'วันเสาร์': 6,
};

export function buildApprovalAdapterInput(input: ApprovalAdapterInput): SubjectApprovalBuilderInput {
  return {
    academicTermId: input.academicTermId,
    subjectId: input.subjectId,
    curriculumTypeId: input.curriculumTypeId,
    courseunit: toNullableString(input.courseunit),
    createdById: input.createdById,
    section: toNullableString(input.section.section) ?? '',
    teachingTypeId: toNumberOrNull(input.section.teachingType),
    studyModeCode: resolveStudyModeCode(
      input.studyModeId,
      input.studyModeOptions,
      input.isProjectResearch
    ),
    isWorkloadSplit: input.workloadMode === 'shared',
    roomName: toNullableString(input.section.room),
    studentCount: input.section.studentCount,
    teachingTimes: input.section.teachingTimes.map((time) => ({
      dayOfWeek: getThaiDayOfWeek(time.teachingDay),
      startTime: toNullableString(time.startTime),
      endTime: toNullableString(time.endTime),
    })),
    teachers: input.teachers.map((teacher) => toApprovalTeacherInput(
      teacher,
      input.scheduleSubmissionUsersByUserId
    )),
  };
}

function toNumberOrNull(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
}

export function resolveStudyModeCode(
  studyModeId: number | null,
  options: SubjectTypeOption[],
  isProjectResearch: boolean
): string {
  const code = String(options.find((option) => option.id === studyModeId)?.code ?? '')
    .trim()
    .toUpperCase();

  return code !== '' ? code : isProjectResearch ? 'SPC' : 'REG';
}

export function getApprovalStatusLabel(status?: string | null): string {
  switch (String(status ?? '').trim()) {
    case 'submitted':
      return 'ส่งขอรับรองแล้ว';
    case 'approved':
      return 'รับรองแล้ว';
    case 'rejected':
      return 'ไม่ผ่านการรับรอง';
    case 'revision_requested':
      return 'ส่งกลับแก้ไข';
    default:
      return 'อยู่ระหว่างดำเนินการ';
  }
}

export function getThaiDayOfWeek(value: unknown): number | null {
  const day = toNullableString(value);
  return day == null ? null : THAI_DAY_INDEXES[day] ?? null;
}

function toApprovalTeacherInput(
  row: SectionTeacherRow,
  scheduleSubmissionUsersByUserId: ScheduleSubmissionUsersByUserId
): ApprovalTeacherInput {
  return {
    userId: row.userId,
    scheduleSubmissionId: row.userId == null
      ? null
      : scheduleSubmissionUsersByUserId[row.userId]?.schedule_submission_id ?? null,
    teachingRange: row.teachingRange,
    projectTeachingTimes: (row.projectTeachingTimes ?? []).map((time) => ({
      dayOfWeek: getThaiDayOfWeek(time.teachingDay),
      startTime: time.startTime,
      endTime: time.endTime,
    })),
    primaryAdvisorStudentCount: row.primaryAdvisorStudentCount,
    coAdvisorStudentCount: row.coAdvisorStudentCount,
    description: row.description,
    showInTimetable: row.showInTimetable,
  };
}
